import { useEffect, useRef, useState } from 'react';
import { Box, Button, IconButton, Typography } from '@material-ui/core';
import { CardGiftcard, Close } from '@material-ui/icons';
import { useDispatch, useSelector } from 'react-redux';
import ShareEarnModal from '../share-earn-modal/ShareEarnModal';
import { referralRewardsAvailable } from '../../utils/native-referral-policy';
import { promptInviteCampaign } from '../../utils/invite-link';
import { fetchReferralStats, getActivePlans } from '../../actions';
import { trackReferralFunnel } from '../../utils/tracking';
import {
  daysOf,
  hasPaidSubscription,
  referralRewards,
} from '../../utils/referral-rewards';
import {
  INVITE_PROMPT_CONFIG,
  shouldShowOnSurface,
  recordShow,
  recordDismissal,
} from '../../utils/referral-reach';

const DISMISSAL_STORAGE_KEY = 'referralPromptDismissedAt';

// localStorage can be missing or throw on access (blocked site data, some
// private modes, a full quota). The prompt renders inside the results page, so a
// storage fault must never throw from render or from a click handler.
const STORAGE_UNREADABLE = Symbol('storage unreadable');
const readStorage = (key) => {
  try {
    return globalThis.localStorage.getItem(key);
  } catch {
    return STORAGE_UNREADABLE;
  }
};

const FOCUSABLE = [
  'a[href]', 'button:not([disabled])', 'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])', 'textarea:not([disabled])', '[tabindex]:not([tabindex="-1"])',
].join(', ');

const isAfter = (a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING) !== 0;
const isBefore = (a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_PRECEDING) !== 0;

// Removing the prompt while its X has focus would drop keyboard and screen-reader
// users back at <body>. Move focus to the next focusable element after the
// prompt; if there is none (the diagnostic report, where the prompt comes last),
// to the nearest one before it ("Build practice quiz"); only if the page has no
// other focusable element, to its main heading. preventScroll: dismissing must
// not jump the page (focusing the heading scrolled the report to the top).
const moveFocusPast = (container) => {
  if (!container) return;
  const doc = container.ownerDocument;
  const focusables = Array.from(doc.querySelectorAll(FOCUSABLE))
    .filter((el) => !container.contains(el));
  const next = focusables.find((el) => isAfter(container, el));
  const previous = focusables.filter((el) => isBefore(container, el)).pop();
  let target = next || previous;
  if (!target) {
    target = doc.querySelector('h1');
    if (!target) return;
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
  }
  target.focus({ preventScroll: true });
};

// At or above this score the prompt leads with the win; below it, with who they
// study with. The score no longer decides WHETHER the prompt shows, only its copy.
export const GREAT_SCORE_THRESHOLD = 70;

/**
 * The reward sentence, built only from the referral stats the server returns
 * (the same GET /referral/stats that ShareEarnModal reads), or null to leave the
 * line out. Durations are never hard-coded here: they are server config, so the
 * prompt can never promise more than the server grants. See referral-rewards.js
 * for when a reward counts as on.
 *
 * `paying` (an active paid plan) switches to gift framing: the inviter's reward
 * is a separate grant that runs alongside time they already pay for, so it would
 * be a promise they never notice. Only the friend's reward is stated.
 */
export const rewardLine = (stats, { paying = false } = {}) => {
  const { inviterDays, friendDays } = referralRewards(stats);
  const gift = friendDays !== null
    ? `They get ${daysOf(friendDays)} of free Pro when they sign up with your link.`
    : null;
  if (paying) {
    return gift;
  }
  if (gift && inviterDays !== null) {
    return `${gift} You get ${daysOf(inviterDays)} when they finish their first quiz.`;
  }
  if (inviterDays !== null) {
    return `You get ${daysOf(inviterDays)} of free Pro when a friend you invite finishes`
      + ' their first quiz.';
  }
  return gift;
};

// "Share & Earn" only when a reward for the inviter is actually on screen;
// paying users are sending a gift; otherwise a plain "Invite".
export const inviteButtonLabel = ({ paying, inviterRewardShown }) => {
  if (paying) return 'Send invite';
  return inviterRewardShown ? 'Share & Earn' : 'Invite';
};


// A finite number, or null. null, undefined and '' must not become 0 (Number('')
// is 0), or a score-less surface would be tracked and worded as a 0% score.
const toScore = (score) => {
  if (score === null || score === undefined || score === '') return null;
  const numeric = Number(score);
  return Number.isFinite(numeric) ? numeric : null;
};

/**
 * The prompt's words for a given score. `absite` is true when the quiz is
 * ABSITE-only: ABSITE residents are the main audience and get copy that names
 * their co-residents and the exam. Other exams (Step 1 students are not
 * residents) get the same message without those words.
 */
export const inviteCopy = ({ score, absite = false }) => {
  const numericScore = toScore(score);
  if (numericScore !== null && numericScore >= GREAT_SCORE_THRESHOLD) {
    return {
      key: 'great_score',
      lead: 'Great score!',
      body: absite
        ? 'Know a co-resident prepping for the ABSITE? Invite them to QLearn.'
        : 'Know someone prepping for the same exam? Invite them to QLearn.',
    };
  }
  // No "practice together": QLearn has no shared-practice feature to deliver it.
  return {
    key: 'study_together',
    lead: absite ? 'Studying with co-residents?' : 'Studying with classmates or colleagues?',
    body: 'Invite them to QLearn.',
  };
};

// `surface` names the placement this instance renders in. Only the surfaces in
// INVITE_PROMPT_CONFIG.enabledSurfaces render; any other mount renders nothing.
const ReferralSharePromptContent = ({ score, surface = 'exam_result', absite = false }) => {
  const [dismissed, setDismissed] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  // Latched on the first render and kept for the life of this mount, which is
  // one page view. Without it, recording the show could push the weekly cap to
  // its limit and the very next re-render (opening the share modal, the plan
  // loading) would hide the prompt mid-view and unmount the modal with it.
  const [shown, setShown] = useState(false);
  // Whether the student has actually seen it this view (see the effect below).
  const seenRef = useRef(false);

  const dispatch = useDispatch();
  // The student's subscriptions (not the /plan/true plan list): see
  // hasPaidSubscription for why only these can tell a payer apart.
  const subscriptions = useSelector((state) => state.getActivePlans?.plansList);
  const subscriptionsLoading = useSelector((state) => Boolean(state.getActivePlans?.loading));
  const referralStats = useSelector((state) => state.referral?.stats);
  const statsRequested = useRef(false);
  const planRequested = useRef(false);
  const containerRef = useRef(null);

  // An unreadable role fails closed (staff must never see the prompt); an
  // unreadable snooze reads as "no snooze recorded".
  const role = readStorage('role');
  const dismissedAt = readStorage(DISMISSAL_STORAGE_KEY);
  const visible = !dismissed && role !== STORAGE_UNREADABLE && (shown || shouldShowOnSurface({
    surface,
    score,
    role,
    dismissedAt: dismissedAt === STORAGE_UNREADABLE ? null : dismissedAt,
    subscriptionsLoaded: Array.isArray(subscriptions),
    hasActivePlan: hasPaidSubscription(subscriptions),
    config: INVITE_PROMPT_CONFIG,
  }));

  const copy = inviteCopy({ score, absite });
  const numericScore = toScore(score);
  const eventData = {
    surface,
    ...(numericScore !== null && { score: numericScore }),
    copy: copy.key,
  };

  useEffect(() => {
    if (visible && !shown) {
      setShown(true);
    }
  }, [visible, shown]);

  // A "show" (the referral_prompt_shown event and the weekly-cap entry) counts
  // only once the prompt is actually on screen, at most once per view. On the
  // diagnostic report it sits ~1,600px down at 375px wide, so counting on mount
  // would spend the weekly allowance on views where it was never seen. Where
  // IntersectionObserver is missing (old browsers, jsdom) it counts at once,
  // which is the earlier behaviour.
  const eventDataRef = useRef(eventData);
  eventDataRef.current = eventData;
  useEffect(() => {
    if (!visible || seenRef.current) {
      return undefined;
    }
    const markSeen = () => {
      if (seenRef.current) return;
      seenRef.current = true;
      recordShow(surface);
      trackReferralFunnel('referral_prompt_shown', eventDataRef.current);
    };
    const element = containerRef.current;
    const Observer = globalThis.IntersectionObserver;
    if (!element || typeof Observer !== 'function') {
      markSeen();
      return undefined;
    }
    const observer = new Observer((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        observer.disconnect();
        markSeen();
      }
    }, { threshold: 0.5 });
    observer.observe(element);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  // The prompt never waits for the stats: it shows at once and gains the reward
  // line when they arrive. They are fetched once per view, only when the prompt
  // is actually showing, and not at all if ShareEarnModal already loaded them.
  useEffect(() => {
    if (visible && !referralStats && !statsRequested.current) {
      statsRequested.current = true;
      dispatch(fetchReferralStats());
    }
  }, [visible, referralStats, dispatch]);

  // The reward framing depends on whether the student pays, so it waits for
  // their subscriptions. Create Quiz, quiz history and the plans page load them;
  // if they are not in the store, the prompt asks once, only while showing and
  // only if no load is already in flight.
  const planKnown = Array.isArray(subscriptions);
  useEffect(() => {
    const userId = readStorage('userId');
    if (visible && !planKnown && !subscriptionsLoading && !planRequested.current
        && typeof userId === 'string' && userId && userId !== 'undefined') {
      planRequested.current = true;
      dispatch(getActivePlans(userId));
    }
  }, [visible, planKnown, subscriptionsLoading, dispatch]);

  if (!visible) {
    return null;
  }

  const paying = hasPaidSubscription(subscriptions);
  const reward = planKnown ? rewardLine(referralStats, { paying }) : null;
  // The inviter's own days are in the line only for a free student with an
  // inviter reward on; a friend-only gift line is not an "earn".
  const inviterRewardShown = planKnown && !paying
    && referralRewards(referralStats).inviterDays !== null;
  const buttonLabel = inviteButtonLabel({ paying, inviterRewardShown });
  // Until the subscriptions are known, the share window decides for itself from
  // the store once they arrive, rather than being told "earn" up front.
  let modalFraming;
  if (planKnown) {
    modalFraming = paying ? 'gift' : 'earn';
  }

  const handleShare = () => {
    trackReferralFunnel('referral_prompt_clicked', eventData);
    setShareModalOpen(true);
  };

  const handleDismiss = () => {
    trackReferralFunnel('referral_prompt_dismissed', eventData);
    try {
      globalThis.localStorage.setItem(DISMISSAL_STORAGE_KEY, String(Date.now()));
    } catch {
      // Storage full or blocked: the snooze cannot persist, but the X must still
      // hide the prompt for this view, so carry on to setDismissed below.
    }
    // The per-surface dismissal counter only serves the lever-3 hard stop, so it
    // is written only when that stop is configured (3 dismissals per surface).
    if (INVITE_PROMPT_CONFIG.maxDismissalsBeforeStop != null) {
      recordDismissal(surface);
    }
    moveFocusPast(containerRef.current);
    setDismissed(true);
  };

  return (
    <>
      <Box
        ref={containerRef}
        data-testid="referral-share-prompt"
        role="region"
        aria-label="Invite a friend"
        style={{
          // Theme surface + text via the theme CSS vars (set on document.body by
          // setTheme), mirroring ShareEarnModal (#379). The old hardcoded #e8f5e9
          // stayed light-green in dark mode (~1.1:1 → effectively invisible),
          // which attenuated the live #569 treatment for dark-mode users.
          // --list-bkg + --text-color clear WCAG AA 4.5:1 in BOTH themes
          // (light #000-on-#e0e0e0 ≈ 15.9:1; dark #fff-on-#6d6d6d ≈ 5.2:1).
          display: 'flex', alignItems: 'center', gap: 12,
          // flexWrap lets the Share button (and dismiss) drop to their own line
          // on a phone so the copy is never clipped; both stay reachable. The
          // prompt sits in the page flow, never fixed or overlaid, so it pushes
          // the results down rather than covering them or the next-quiz button.
          flexWrap: 'wrap',
          background: 'var(--list-bkg, #e8f5e9)',
          color: 'var(--text-color, #000)',
          border: '1px solid var(--border-color, transparent)',
          borderRadius: 8,
          padding: '10px 16px', margin: '16px 0'
        }}
      >
        <Typography variant="body2" style={{ flex: '1 1 240px', color: 'inherit' }}>
          <strong>{copy.lead}</strong>{' '}{copy.body}
          {reward && (
            <>
              {' '}
              <span data-testid="referral-reward-line" style={{ display: 'block', marginTop: 4 }}>
                {reward}
              </span>
            </>
          )}
        </Typography>
        {/* Outlined in the prompt's own text colour, so it never outranks the
            page's "Practice more" and stays legible on the prompt's surface in
            both themes (primary blue on the dark-theme grey is ~1.4:1).
            minHeight 44: a phone tap target, like the full-size dismiss button. */}
        <Button
          variant="outlined"
          startIcon={<CardGiftcard />}
          onClick={handleShare}
          style={{ minHeight: 44, color: 'inherit', borderColor: 'currentColor' }}
        >
          {buttonLabel}
        </Button>
        <IconButton
          aria-label="Dismiss share prompt"
          onClick={handleDismiss}
          style={{ color: 'var(--text-color, rgba(0,0,0,0.54))' }}
        >
          <Close fontSize="small" />
        </IconButton>
      </Box>
      {/* The share window repeats this prompt's framing decision, once made. */}
      <ShareEarnModal
        open={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        absite={absite}
        framing={modalFraming}
        campaign={promptInviteCampaign(surface)}
      />
    </>
  );
};

// iOS ruling B5: the native app shows no invite prompt on any surface, so it also logs no prompt events
// and the web's referral numbers stay web-only.
const ReferralSharePrompt = (props) => (
  referralRewardsAvailable() ? <ReferralSharePromptContent {...props} /> : null
);

export default ReferralSharePrompt;
